import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import QuoteModal from '../../components/feature/QuoteModal';
import HeroCarousel from '../../components/feature/HeroCarousel';
import ServiceModal, { ServiceItem } from '../../components/feature/ServiceModal';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import ShowreelSection from '@/components/feature/ShowreelSection';
import SocialSidebar from '@/components/feature/SocialSidebar';
import PortfolioLightbox, { PortfolioItem } from '@/components/feature/PortfolioLightbox';
import { CONTACT, decodeContact, whatsappHref } from '@/lib/contactProtect';

const EN_SLIDES = [
  {
    subtitle: 'Professional Event Solutions',
    title: 'Transforming Vision into Experience',
    description:
      'With more than 35 years of experience, we deliver world-class event management and technical production services across Turkey, Europe, the Middle East, and the Caucasus.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/7fe880b7ed4d0915e58be95dc2eb2824.jfif',
  },
  {
    subtitle: 'Technical Excellence',
    title: 'Flawless Technical Production',
    description:
      'From sound systems and lighting to video production and stage design — our expert technical team ensures every detail is executed to perfection.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/88a241c59883f830429183d4ef769bd3.jfif',
  },
  {
    subtitle: 'Global Reach',
    title: 'Events Across 17+ Countries',
    description:
      'Operating in Turkey, Hungary, UAE, Germany, Austria, Saudi Arabia, Georgia, Azerbaijan and beyond — we bring local expertise with a global perspective.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/3a9d5a33d4f40290002e787392d5bfac.jfif',
  },
  {
    subtitle: 'Trusted by Industry Leaders',
    title: 'Over 500 Events Delivered',
    description:
      'From NATO Summits to Huawei Technology Summits, Coca-Cola tours to pharmaceutical congresses — we have earned the trust of the world\'s leading brands.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/c6534210e3a604c303c99e2a10d64a17.jfif',
  },
  {
    subtitle: 'End-to-End Service',
    title: 'Concept to Execution',
    description:
      'We handle everything — venue sourcing, logistics, AV setup, live coordination, and post-event media production — so you can focus on your vision.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/ce6c19bec576ecf4f881f0cd130305a3.jfif',
  },
  {
    subtitle: 'Your Partner in Success',
    title: '24/7 Support & Dedicated Team',
    description:
      'Our dedicated team is available around the clock to ensure your event runs smoothly from the first planning call to the final curtain.',
    image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/138555603754b0beeffc9f2035e517f0.jfif',
  },
  {
    subtitle: 'Meet the Team Behind the Vision',
    title: 'Family, Passion & Dedication',
    description:
      'Behind every great event is a team that truly cares. Meet the people who bring passion, creativity, and dedication to every project we deliver.',
    image: 'https://storage.readdy-site.link/project_files/9e58cc21-40d2-414d-b7dd-441fa32130a9/3bf2c6e4-476e-493b-829d-1563efc1f1a7_A5BDC899-91CB-4038-BC89-433CE131BC45_1_105_c.jpeg?v=f5bc227e18cc9c95b97a462dba01784a',
  },
];

const EN_PORTFOLIO: PortfolioItem[] = [
  { title: '2025 Vertigo Academy', location: 'Budapest, Hungary', category: 'Pharmaceutical Congress', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/88a241c59883f830429183d4ef769bd3.jfif' },
  { title: 'Huawei OTF 2024', location: 'Istanbul, Türkiye', category: 'Technology Summit', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/7fe880b7ed4d0915e58be95dc2eb2824.jfif' },
  { title: "Hytera's Global Partner Summit MEA 2024", location: 'Dubai, UAE', category: 'Corporate Summit', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/3a9d5a33d4f40290002e787392d5bfac.jfif' },
  { title: '2023 Prag Nutricia EXPO 2.0 Event', location: 'Prag, Czech Republic', category: 'Medical Conference', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/138555603754b0beeffc9f2035e517f0.jfif' },
  { title: 'Coca-Cola Türkiye Sound Wave Tours Nationwide', location: 'Türkiye', category: 'Music Tour', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/ce6c19bec576ecf4f881f0cd130305a3.jfif' },
  { title: '2004 NATO Summit Istanbul', location: 'Istanbul, Türkiye', category: 'International Summit', image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/c6534210e3a604c303c99e2a10d64a17.jfif' },
];

const EN_BADGES = [
  { icon: 'ri-tools-line', text: 'Own Technical Equipment' },
  { icon: 'ri-global-line', text: 'Extensive Partner Network' },
  { icon: 'ri-translate-2', text: 'Multinational Service' },
  { icon: 'ri-24-hours-line', text: '24/7 Support' },
];

const EN_SERVICE_SLUGS: Record<string, string> = {
  'Technical Production & AV': 'technical-production-av',
  'Corporate Events & Conferences': 'corporate-events-conferences',
  'Stage Design & Lighting': 'stage-design-lighting',
  'DMC / Destination Services': 'dmc-destination-services',
  'Hybrid / Virtual Events': 'hybrid-virtual-events',
  'Product Launches & Brand Activations': 'product-launches-brand-activations',
  'Award Ceremonies & Galas': 'award-ceremonies-galas',
  'Post-Event Media Production': 'post-event-media-production',
};

const EN_SERVICES: ServiceItem[] = [
  {
    icon: 'ri-lightbulb-line',
    title: 'Event Strategy & Planning',
    description: 'From vision to timeline.',
    detail:
      'We craft your event\'s complete roadmap — from initial concept and budget planning to detailed timelines, vendor coordination, and contingency strategies. Every event starts with a solid plan.',
  },
  {
    icon: 'ri-mic-line',
    title: 'Technical Production & AV',
    description: 'Sound, lighting, video, stage — seamlessly integrated.',
    detail:
      'Our in-house technical team handles all audiovisual requirements: professional sound systems, dynamic lighting rigs, LED video walls, projection mapping, and full stage construction. We own our equipment for maximum reliability.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Venue Sourcing & Logistics',
    description: 'We find the right location and manage all logistics.',
    detail:
      'From iconic ballrooms in Istanbul to conference centers in Budapest and beachfront venues in Dubai — we source, negotiate, and manage the perfect venue for your event, including all on-site logistics.',
  },
  {
    icon: 'ri-live-line',
    title: 'Live Event Execution',
    description: 'On-site coordination, flawless delivery.',
    detail:
      'Our experienced event managers are on-site from setup to teardown, coordinating every team, vendor, and timeline to ensure your event runs without a hitch — no matter the scale.',
  },
  {
    icon: 'ri-global-line',
    title: 'DMC / Destination Services',
    description: 'Complete destination management and local expertise.',
    detail:
      'As a Destination Management Company, we provide comprehensive local services: hotel sourcing, airport transfers, guided tours, cultural experiences, and full ground logistics in every country we operate.',
  },
  {
    icon: 'ri-computer-line',
    title: 'Hybrid / Virtual Events',
    description: 'Engage both physical and digital audiences.',
    detail:
      'We design and produce hybrid and fully virtual events with professional live streaming, interactive platforms, virtual networking, and broadcast-quality production to connect audiences worldwide.',
  },
  {
    icon: 'ri-palette-line',
    title: 'Stage Design & Lighting',
    description: 'Creative stage and lighting solutions that captivate.',
    detail:
      'Our creative team designs stunning stage environments — from minimalist corporate setups to elaborate theatrical productions — using the latest in LED technology, moving lights, and custom scenic elements.',
  },
  {
    icon: 'ri-camera-line',
    title: 'Post-Event Media Production',
    description: 'Professional photography and videography services.',
    detail:
      'We capture every moment with professional photography and cinematic videography, delivering edited highlight reels, full event recordings, and social media content packages after your event.',
  },
  {
    icon: 'ri-group-line',
    title: 'Corporate Events & Conferences',
    description: 'Professional corporate gatherings and business conferences.',
    detail:
      'From intimate board meetings to large-scale international conferences, we manage every aspect of corporate events — AV, catering coordination, simultaneous interpretation, and delegate management.',
  },
  {
    icon: 'ri-music-line',
    title: 'Entertainment & Artist Management',
    description: 'Complete artist booking and entertainment coordination.',
    detail:
      'We handle all aspects of entertainment — artist booking, contract negotiation, technical riders, backstage management, and on-site coordination — ensuring a seamless performance experience.',
  },
  {
    icon: 'ri-award-line',
    title: 'Award Ceremonies & Galas',
    description: 'Elegant award shows and prestigious ceremonies.',
    detail:
      'We produce memorable award ceremonies and gala dinners with sophisticated stage design, custom trophies, red carpet setups, live entertainment, and impeccable event flow management.',
  },
  {
    icon: 'ri-presentation-line',
    title: 'Product Launches & Brand Activations',
    description: 'Impactful product reveals and immersive brand experiences.',
    detail:
      'We create buzz-worthy product launches and brand activations that leave lasting impressions — combining creative staging, experiential design, media coverage coordination, and audience engagement strategies.',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Festival & Cultural Events',
    description: 'Large-scale festivals and cultural celebrations.',
    detail:
      'From music festivals to cultural heritage events, we manage large-scale outdoor and indoor productions with full technical infrastructure, crowd management planning, and multi-stage coordination.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Exhibition & Trade Shows',
    description: 'Professional exhibition management and trade show coordination.',
    detail:
      'We design and manage exhibition stands, trade show logistics, and product display environments — handling everything from booth construction to visitor flow management and lead capture systems.',
  },
  {
    icon: 'ri-team-line',
    title: 'Team Building & Incentive Events',
    description: 'Engaging team activities and motivational corporate retreats.',
    detail:
      'We design creative team building programs and incentive travel experiences that motivate, inspire, and strengthen team bonds — from adventure activities to luxury retreats across our global destinations.',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Educational & Training Events',
    description: 'Workshops, seminars, and educational conference management.',
    detail:
      'We manage educational events of all sizes — from intimate workshops to large-scale training conferences — with professional AV, breakout room management, e-learning integration, and attendee engagement tools.',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [showCallOptions, setShowCallOptions] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);

  useSeoMeta({
    lang: 'en',
    canonicalPath: '/',
    title: 'Can ÖZEN Technical Production | Corporate Event AV Services Istanbul Budapest Dubai',
    description:
      'Professional AV services and technical production for corporate events in Turkey, Europe & Dubai. Sound systems, lighting, video production, stage design, airport transfers, event logistics. 35+ years experience serving Istanbul, Budapest, Dubai, Germany.',
    keywords:
      'AV services Turkey, technical production Istanbul, corporate event AV Europe, sound systems lighting Turkey, event logistics Dubai',
    ogLocale: 'en_US',
  });

  useEffect(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-17649667115/-OOwCNr32IwcEKuYguBB',
      });
    }
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Can ÖZEN Technical Production | Corporate Event AV Services Istanbul Budapest Dubai',
      description: 'Professional AV services and technical production for corporate events in Turkey, Europe & Dubai.',
      url: siteUrl,
      inLanguage: 'en',
      dateModified: '2026-03-20',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl }],
      },
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'CSG The Event Company & IEM101 International',
      alternateName: 'Can ÖZEN Technical Production',
      url: siteUrl,
      logo: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/afb0395a8f917252ae1628849c248ce7.png',
      image: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/afb0395a8f917252ae1628849c248ce7.png',
      description: 'Professional AV services and technical production for corporate events in Turkey, Europe & Dubai. 35+ years experience.',
      // Telefon semadan cikarildi, bkz. lib/contactProtect.ts
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Tomtom Mah. İstiklal Cad. No:189 Kat:2 Daire:3',
        addressLocality: 'Beyoğlu, Istanbul',
        addressRegion: 'Istanbul',
        addressCountry: 'TR',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 41.0082, longitude: 28.9784 },
      sameAs: [
        'https://www.linkedin.com/in/can-%C3%B6zen-8b15667/',
        'https://www.instagram.com/csgtheeventcompany/',
      ],
      priceRange: '$$',
      areaServed: ['Turkey', 'Hungary', 'UAE', 'Germany', 'Austria', 'Saudi Arabia', 'Georgia', 'Azerbaijan'],
    };

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'CSG The Event Company & IEM101 International',
      alternateName: ['Can ÖZEN Technical Production', 'CSG Event Company', 'IEM101'],
      url: siteUrl,
      logo: 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/afb0395a8f917252ae1628849c248ce7.png',
      description: 'Professional AV services and technical production for corporate events in Turkey, Europe & Dubai. 35+ years experience.',
      foundingDate: '1989',
      founder: { '@type': 'Person', name: 'Can Ozen' },
      address: [
        {
          '@type': 'PostalAddress',
          streetAddress: 'Tomtom Mah. İstiklal Cad. No:189 Kat:2 Daire:3',
          addressLocality: 'Beyoğlu',
          addressRegion: 'Istanbul',
          addressCountry: 'TR',
          name: 'Istanbul Office',
        },
        {
          '@type': 'PostalAddress',
          streetAddress: 'Föld utca 53',
          addressLocality: 'Budapest',
          postalCode: '1032',
          addressCountry: 'HU',
          name: 'Budapest Office',
        },
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          // Telefon semadan cikarildi, bkz. lib/contactProtect.ts
          contactType: 'customer service',
          areaServed: ['TR', 'ME', 'EU'],
          availableLanguage: ['English', 'Turkish'],
        },
      ],
      sameAs: [
        'https://www.linkedin.com/in/can-%C3%B6zen-8b15667/',
        'https://www.instagram.com/csgtheeventcompany/',
      ],
      serviceArea: { '@type': 'Place', name: 'Europe, Middle East, Caucasus' },
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AV Services and Technical Production for Corporate Events',
      provider: { '@type': 'Organization', name: 'CSG The Event Company & IEM101 International' },
      serviceType: 'AV Services',
      description: 'Professional audiovisual services and technical production for corporate events.',
      areaServed: [
        { '@type': 'Country', name: 'Turkey' },
        { '@type': 'Country', name: 'Hungary' },
        { '@type': 'Country', name: 'United Arab Emirates' },
        { '@type': 'Country', name: 'Germany' },
        { '@type': 'Country', name: 'Austria' },
        { '@type': 'Country', name: 'Saudi Arabia' },
        { '@type': 'Country', name: 'Georgia' },
        { '@type': 'Country', name: 'Azerbaijan' },
      ],
    };

    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Can Ozen',
      jobTitle: 'Founder & CEO',
      worksFor: { '@type': 'Organization', name: 'CSG The Event Company & IEM101 International' },
      description: 'AV services and technical production expert with 35+ years of experience.',
      sameAs: ['https://www.linkedin.com/in/can-%C3%B6zen-8b15667/'],
    };

    const addSchema = (schema: object, id: string) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(webPageSchema, 'webpage-schema');
    addSchema(localBusinessSchema, 'localbusiness-schema');
    addSchema(organizationSchema, 'organization-schema');
    addSchema(serviceSchema, 'service-schema');
    addSchema(personSchema, 'person-schema');

    return () => {
      ['webpage-schema', 'localbusiness-schema', 'organization-schema', 'service-schema', 'person-schema'].forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, []);

  const handleCallClick = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'phone_call_button_click', { event_category: 'engagement', event_label: 'header_call_button', value: 1 });
    }
    setShowCallOptions(true);
  };

  const handlePhoneCall = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'phone_call_direct', { event_category: 'conversion', event_label: 'direct_phone_call', value: 1 });
      window.gtag('event', 'conversion', { send_to: 'AW-17649667115/FS49CJyah64bEKuYguBB' });
    }
    window.location.href = `tel:${decodeContact(CONTACT.istanbulPhoneRaw)}`;
    setShowCallOptions(false);
  };

  const handleWhatsAppCall = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'whatsapp_call', { event_category: 'conversion', event_label: 'whatsapp_contact', value: 1 });
      window.gtag('event', 'conversion', { send_to: 'AW-17649667115/FS49CJyah64bEKuYguBB' });
    }
    window.open(whatsappHref(), '_blank');
    setShowCallOptions(false);
  };

  const handleAnalyticsAccess = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'analytics_access', { event_category: 'admin', event_label: 'analytics_page_access', value: 1 });
    }
    navigate('/analytics');
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SocialSidebar lang="en" />
      <Header />

      {/* Hero Carousel */}
      <HeroCarousel
        slides={EN_SLIDES}
        onQuoteOpen={() => setIsQuoteOpen(true)}
        onCallClick={handleCallClick}
        ctaQuote="Request a Quote"
        ctaCall="Call Us Now"
        badges={EN_BADGES}
      />

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Our Story &amp;
                <span className="text-yellow-400"> Vision</span>
              </h2>
              <div className="space-y-4 text-gray-300">
                <p className="text-lg">
                  My journey began in 1989 as an AV technician. Over the years, I nurtured my passion
                  and in 1996 founded AVS İletişim. In 2002, I launched CSG The Event Company,
                  executing national and international projects across Turkey.
                </p>
                <p className="text-lg">
                  In 2020, I expanded to Budapest and founded{' '}
                  <strong className="text-yellow-400">IEM101 International Kft</strong>,
                  scaling operations across Europe and beyond. Through CSG Teknik Prodüksiyon A.Ş. in Istanbul,
                  and IEM101 in Hungary, we deliver unmatched event excellence from concept to execution.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">35+</div>
                  <div className="text-sm text-gray-400">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">500+</div>
                  <div className="text-sm text-gray-400">Events Delivered</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">15+</div>
                  <div className="text-sm text-gray-400">Countries</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/b075e0d390815ed30f85d3182066e5d5.png"
                alt="Can Ozen and team - CSG The Event Company"
                className="rounded-2xl shadow-2xl object-cover object-top w-full h-96 lg:h-[500px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              What We Do &amp;
              <span className="text-yellow-400"> Our Capabilities</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From concept to execution, we provide comprehensive event solutions
              that transform your vision into unforgettable experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {EN_SERVICES.map((service, index) => {
              const slug = EN_SERVICE_SLUGS[service.title];
              const cardContent = (
                <>
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-xl text-black`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{service.description}</p>
                  <span className="text-yellow-400 text-xs font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Learn more <i className="ri-arrow-right-line"></i>
                  </span>
                </>
              );
              return slug ? (
                <Link
                  key={index}
                  to={`/services/${slug}`}
                  className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-yellow-400/50 transition-all duration-300 hover:scale-105 cursor-pointer group block"
                >
                  {cardContent}
                </Link>
              ) : (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-yellow-400/50 transition-all duration-300 hover:scale-105 cursor-pointer group"
                  onClick={() => setSelectedService(service)}
                >
                  {cardContent}
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-3 border border-yellow-400/50 text-yellow-400 font-semibold rounded-lg hover:bg-yellow-400/10 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Selected
              <span className="text-yellow-400"> Projects</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover some of our most impactful events across Europe, Middle East, and beyond.
            </p>
          </div>

          <PortfolioLightbox lang="en" items={EN_PORTFOLIO} />

          <div className="mt-16 text-center">
            <h3 className="text-xl font-semibold text-white mb-4">Want to Learn More About Our Projects?</h3>
            <p className="text-gray-300 mb-6">
              If you would like detailed information about our projects, you can reach us through our contact information below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Get In
              <span className="text-yellow-400"> Touch</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to transform your vision into an unforgettable experience?
              Let's discuss your next event.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6">
                <h4 className="text-xl font-semibold mb-4 text-yellow-400">Global Reach</h4>
                <p className="text-gray-300 mb-4">We operate across:</p>
                <div className="flex flex-wrap gap-2">
                  {['Türkiye', 'Hungary', 'Saudi Arabia', 'Georgia', 'Azerbaijan', 'Austria', 'UAE', 'Greece', 'Portugal', 'Germany', 'Dubai', 'Czech Republic', 'Serbia', 'Spain', 'France', 'Holland', 'Italy', 'England'].map((country) => (
                    <span key={country} className="bg-yellow-400/10 text-yellow-400 px-3 py-1 rounded-full text-sm">
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-500/5 border border-yellow-400/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-yellow-400 rounded-xl flex items-center justify-center">
                    <i className="ri-file-text-line text-xl text-black"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">Get a Custom Quote</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Tell us about your event and receive a tailored proposal within 24 hours.
                    </p>
                    <button
                      onClick={() => setIsQuoteOpen(true)}
                      className="w-full py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all duration-300 hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                    >
                      <i className="ri-send-plane-line"></i>
                      Request a Quote
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-gray-800 border border-gray-700 rounded-xl flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-yellow-400"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">Prefer to Talk?</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Reach us directly by phone or WhatsApp for a quick conversation.
                    </p>
                    <button
                      onClick={handleCallClick}
                      className="w-full py-3 border border-gray-600 text-gray-200 font-semibold rounded-lg hover:border-yellow-400/50 hover:text-yellow-400 transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                    >
                      <i className="ri-phone-line"></i>
                      Call Us Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-yellow-400">Istanbul Office</h3>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <i className="ri-building-line text-yellow-400 mr-3 mt-1"></i>
                  <div>
                    <div className="font-medium">CSG Etkinlik Yönetim ve Teknik Prodüksiyon Hizmetleri A.Ş.</div>
                    <div>Tomtom Mah. İstiklal Cad. No:189 Kat:2 Daire:3</div>
                    <div>Beyoğlu/İstanbul, Türkiye</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-yellow-400">Budapest Office</h3>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <i className="ri-building-line text-yellow-400 mr-3 mt-1"></i>
                  <div>
                    <div className="font-medium">IEM101 International Kft.</div>
                    <div>Föld utca 53</div>
                    <div>1032 Budapest, Hungary</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showreel Section */}
      <ShowreelSection
        subtitle="Watch Our Work"
        title="See the Magic in Motion"
        description="From grand stages to intimate gatherings — watch how we bring events to life with precision, creativity, and passion."
        ctaLabel="Play Showreel"
        closeHint="Press ESC or click outside to close"
      />

      <QuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />

      {/* Service Detail Modal */}
      <ServiceModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
        ctaLabel="Request a Quote"
        onCtaClick={() => setIsQuoteOpen(true)}
      />

      {/* Call Options Modal */}
      {showCallOptions && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Give us a call</h3>
              <p className="text-gray-400">How would you like to get in touch?</p>
            </div>
            <div className="space-y-3">
              <button onClick={handlePhoneCall} className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-3 transition-colors cursor-pointer">
                <i className="ri-phone-line text-xl"></i><span>Voice Call</span>
              </button>
              <button onClick={handleWhatsAppCall} className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-3 transition-colors cursor-pointer">
                <i className="ri-whatsapp-line text-xl"></i><span>WhatsApp</span>
              </button>
              <button onClick={() => setShowCallOptions(false)} className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded-lg transition-colors cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}



      <Footer lang="en" />
    </div>
  );
}
